import { applyPatch } from 'fast-json-patch';
import produce from 'immer';
import { flatten, get, isEqual, set, template } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { Subscription } from 'rxjs';

import { request } from '@swf/core/api/request';
import { requestStream } from '@swf/core/api/requestStream';
import { subscribe } from '@swf/core/api/subscribe';
import usePrevious from '@swf/core/hooks/usePrevious';
import { select } from '@swf/core/store';
import type { Dispatch, RootState } from '@swf/core/store/types';
import type { FetchServiceMeta, WidgetContainer } from '@swf/core/types/Widget';
import { fetchProperties, loadWidget } from '@swf/core/utils/widgets';

type Unsubscribe = (serviceKey: string) => void;
type FetchService = (
  key: string,
  params?: Record<string, string>,
  meta?: FetchServiceMeta
) => Promise<string>;
interface Subscriptions {
  [key: string]: { [destination: string]: Subscription };
}

const useFetchService = (
  widgetConfig: WidgetContainer,
  updateTrackingContext: () => Promise<void>
): [Subscriptions, Unsubscribe, FetchService] => {
  const { getState } = useStore<RootState>();
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const hasClientSession = useSelector(select.session.hasClientSession);
  const dispatch = useDispatch<Dispatch>();
  const initialServicesFetched = useRef(false);
  const subscriptionsRef = useRef<Subscriptions>({});
  const wasAuthenticated = usePrevious(isAuthenticated);
  const authChanged = wasAuthenticated !== isAuthenticated;
  const { services = {} } = widgetConfig;
  const prevServices = usePrevious(services);
  const servicesChanged = !isEqual(prevServices, services);
  const hasData = useSelector(
    (rootState: RootState) => !!select.widgets.getData(rootState, widgetConfig.id)
  );

  const unsubscribe = useCallback<Unsubscribe>((serviceKey) => {
    const subscriptions = subscriptionsRef.current;
    if (subscriptions[serviceKey]) {
      Object.values(subscriptions[serviceKey]).forEach((sub) => sub.unsubscribe());
      delete subscriptions[serviceKey];
    } else {
      Object.keys(subscriptions).forEach((key) => {
        const [name] = Object.keys(subscriptions[key]).filter((sub) => sub === serviceKey);
        if (name) {
          subscriptions[key][name].unsubscribe();
          delete subscriptions[key][name];
        }
      });
    }
  }, []);

  const fetchService = useCallback<FetchService>(
    async (key, params?, meta = {}): Promise<string> => {
      const subscriptions = subscriptionsRef.current;
      const { type, id, services = {} } = widgetConfig;

      if (!services[key]) {
        return key;
      }

      const { serviceReducers } = await fetchProperties(type);
      const { params: defaults, config } = services[key];
      const { templateValues, reducer: metaReducer, ...rest } = meta;
      const name = templateValues ? template(config.name)(templateValues) : config.name;
      const { onAuth, onLoad } = config;
      const rootState = getState();
      const isAuthenticated = select.session.isAuthenticated(rootState);
      const options = select.site.getRequestOptions(rootState);

      if (!onLoad && onAuth && !isAuthenticated) {
        return name;
      }

      const reducer = get(serviceReducers, key, metaReducer);
      const payload = { id, key, config, meta: rest };

      if (config.requestStream) {
        let initialData = get(select.widgets.getData(rootState, id), key);
        const refresh = !!subscriptions[key];
        const requestBody = { service: name, body: params || defaults, ...options };
        unsubscribe(key);

        if (initialData && meta.patch) {
          const { body } = await request(requestBody);
          initialData = produce(initialData, (draft: typeof initialData) => {
            applyPatch(draft, body);
          });
          dispatch.widgets.response({ ...payload, data: initialData });
        } else if (refresh) {
          initialData = undefined;
        }

        set(
          subscriptions,
          [key, name],
          requestStream(requestBody, initialData).subscribe((data) =>
            dispatch.widgets.response({ ...payload, data })
          )
        );
      } else if (config.subscribe && !get(subscriptions, [key, name])) {
        set(
          subscriptions,
          [key, name],
          subscribe(name).subscribe(({ body }) =>
            dispatch.widgets.response({ ...payload, data: body, reducer })
          )
        );
      } else if (!config.subscribe) {
        await dispatch.widgets.fetchData({ widgetConfig, key, meta, params });
      }

      await updateTrackingContext();
      return name;
    },
    [dispatch.widgets, getState, unsubscribe, updateTrackingContext, widgetConfig]
  );

  const initialFetch = useCallback(async () => {
    if (!hasData || isAuthenticated) {
      await loadWidget(widgetConfig, { isAuthenticated, dispatch });
    }

    Object.keys(services).forEach((key) => {
      const {
        config: { onAuth, onLoad, subscribe, requestStream }
      } = services[key];
      if ((subscribe || requestStream) && (onLoad || (onAuth && isAuthenticated))) {
        fetchService(key);
      }
    });
  }, [dispatch, fetchService, hasData, isAuthenticated, services, widgetConfig]);

  useEffect(() => {
    if (isAuthenticated && !hasClientSession) return;

    if (!initialServicesFetched.current) {
      initialFetch();
      initialServicesFetched.current = true;
      return;
    }

    Object.keys(services).forEach((key) => {
      const {
        config: { onAuth, onLoad, subscribe, requestStream }
      } = services[key];

      if ((onAuth && authChanged) || servicesChanged) {
        if ((subscribe || (requestStream && !onLoad)) && onAuth && !isAuthenticated) {
          unsubscribe(key);
          dispatch.widgets.clear({ id: widgetConfig.id, key });
        } else {
          fetchService(key);
        }
      }
    });
  }, [
    authChanged,
    dispatch.widgets,
    fetchService,
    hasClientSession,
    initialFetch,
    isAuthenticated,
    services,
    servicesChanged,
    unsubscribe,
    widgetConfig.id
  ]);

  useEffect(() => {
    const subscriptions = subscriptionsRef.current;
    return () => {
      const subs = flatten(Object.values(subscriptions).map((item) => Object.values(item)));
      Object.values(subs).forEach((sub) => sub.unsubscribe());
    };
  }, []);

  return [subscriptionsRef.current, unsubscribe, fetchService];
};

export default useFetchService;
